import React, { useState, useEffect } from "react";
import { List, Skeleton, Collapse, Button } from "antd";
import { QuestionCircleOutlined, MessageOutlined } from "@ant-design/icons";
import RightDrawerTemplate from "./RightDrawerTemplate";
import {
  _get_faqs,
  _get_business_support_team,
  _get_all_support_executives,
} from "../common/axios_services";
import whatsappIcon from "../assets/images/icons/whatsapp.png";

const { Panel } = Collapse;

const HelpSupportButton = ({ page, businessId, orderId }) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [supportTeam, setSupportTeam] = useState(null);
  const [supportExecutives, setSupportExecutives] = useState([]);

  const toggleDrawer = () => setOpenDrawer(!openDrawer);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const response = await _get_faqs(page);
      setFaqs(response?.data?.data?.data || response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupport = async () => {
    try {
      if (businessId) {
        const response = await _get_business_support_team(businessId);
        setSupportTeam(response.data);
      }
      if (page === "DRIVER_HOME" || page === "MY_ORDER") {
        const response = await _get_all_support_executives();
        setSupportExecutives(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching support data:", error);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, [page, businessId, orderId]);

  useEffect(() => {
    if (openDrawer) {
      fetchSupport();
    }
  }, [openDrawer]);

  const handleChat = (phoneNumber) => {
    if (!phoneNumber) return;
    const formattedPhone = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+234${phoneNumber.startsWith("0") ? phoneNumber.slice(1) : phoneNumber}`;
    window.open(`https://wa.me/${formattedPhone.replace(/\D/g, "")}`, "_blank");
  };

  const genericChatLink =
    "https://konnect.ws/contact";

  const availableFaqs = Array.isArray(faqs) ? faqs : [];

  // Hide the button if there are no FAQs (unless we are in a special support mode with businessId/orderId)
  if (!loading && availableFaqs.length === 0 && !businessId && !orderId) {
    return null;
  }

  return (
    <>
      <div
        className="help-support-button-wrapper"
        onClick={toggleDrawer}
        style={{
          position: "fixed",
          bottom: "80px",
          right: "20px",
          zIndex: 1000,
          backgroundColor: "#00B55B",
          width: "50px",
          height: "50px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
          cursor: "pointer",
        }}
      >
        <QuestionCircleOutlined style={{ fontSize: "24px" }} />
      </div>

      <RightDrawerTemplate
        openDrawer={openDrawer}
        closeDrawer={toggleDrawer}
        title="Help & Support"
        width="100%"
      >
        <div className="help-drawer-content">
          <div className="faq-section mb-8">
            <h3 className="section-title text-xl font-bold mb-4">
              Frequently Asked Questions
            </h3>
            {loading ? (
              <Skeleton active />
            ) : availableFaqs.length > 0 ? (
              <Collapse
                accordion
                expandIconPosition="right"
                className="bg-transparent border-none"
                items={availableFaqs.map((faq) => ({
                  key: faq.id,
                  label: (
                    <span className="font-semibold text-gray-800">
                      {faq.question}
                    </span>
                  ),
                  children: (
                    <div className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  ),
                  style: {
                    background: "#f8fafc",
                    borderRadius: "12px",
                    marginBottom: "16px",
                    border: "1px solid #f1f5f9",
                  },
                }))}
              />
            ) : (
              <p className="text-gray-500">No FAQs available for this page.</p>
            )}
          </div>

          <div className="chat-support-section mt-10">
            <h3 className="section-title text-xl font-bold mb-4">
              Need more help?
            </h3>
            <div className="support-options grid gap_3">
              {supportTeam?.supportOfficer && (
                <div className="support-card p_3 border rounded_lg flex items_center justify_between">
                  <div>
                    <h4 className="font_bold">
                      {supportTeam.supportOfficer.firstName} (Support Officer)
                    </h4>
                    <p className="text_sm text_gray-500">
                      Assigned specifically to your business
                    </p>
                  </div>
                  <Button
                    type="primary"
                    icon={
                      <img
                        src={whatsappIcon}
                        alt="WA"
                        width={16}
                        className="mr_2"
                      />
                    }
                    onClick={() =>
                      handleChat(supportTeam.supportOfficer.phoneNumber)
                    }
                    style={{
                      backgroundColor: "#25D366",
                      borderColor: "#25D366",
                    }}
                  >
                    Chat
                  </Button>
                </div>
              )}

              {supportTeam?.supportManager && (
                <div className="support-card p_3 border rounded_lg flex items_center justify_between">
                  <div>
                    <h4 className="font_bold">
                      {supportTeam.supportManager.firstName} (Support Manager)
                    </h4>
                    <p className="text_sm text_gray-500">Oversees your zone</p>
                  </div>
                  <Button
                    type="primary"
                    icon={
                      <img
                        src={whatsappIcon}
                        alt="WA"
                        width={16}
                        className="mr_2"
                      />
                    }
                    onClick={() =>
                      handleChat(supportTeam.supportManager.phoneNumber)
                    }
                    style={{
                      backgroundColor: "#25D366",
                      borderColor: "#25D366",
                    }}
                  >
                    Chat
                  </Button>
                </div>
              )}

              {supportExecutives.length > 0 && (
                <div className="executives-section">
                  <p className="text_sm font_semibold mb_2">
                    Support Executives:
                  </p>
                  <List
                    dataSource={supportExecutives}
                    renderItem={(exec) => (
                      <List.Item className="border_b_0 py_2">
                        <div className="flex w_full items_center justify_between">
                          <span>
                            {exec.firstName} {exec.lastName}
                          </span>
                          <Button
                            size="small"
                            type="primary"
                            icon={
                              <img
                                src={whatsappIcon}
                                alt="WA"
                                width={14}
                                className="mr_2"
                              />
                            }
                            onClick={() => handleChat(exec.phoneNumber)}
                            style={{
                              backgroundColor: "#25D366",
                              borderColor: "#25D366",
                            }}
                          >
                            Chat
                          </Button>
                        </div>
                      </List.Item>
                    )}
                  />
                </div>
              )}

              <div className="general-support mt_3">
                <Button
                  block
                  size="large"
                  type="primary"
                  icon={<MessageOutlined />}
                  onClick={() => window.open(genericChatLink, "_blank")}
                  className="btn-base"
                >
                  Contact us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </RightDrawerTemplate>
    </>
  );
};

export default HelpSupportButton;
